import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Code2, BookOpen, X, Menu, Video, BrainCircuit, Trophy, HelpCircle, Globe, Users } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTour } from '../../contexts/TourContext';

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const { startTour } = useTour();
  
  const isActive = (path: string) => 
    location.pathname === path ? 'text-[#512BD4]' : 'text-gray-600 hover:text-[#512BD4]';

  const navItems = [
    { path: '/', label: t('nav.home'), icon: Code2 },
    { path: '/quiz', label: t('nav.quiz'), icon: BrainCircuit },
    { path: '/coding-challenge', label: t('nav.challenges'), icon: Trophy },
    { path: '/interviews', label: t('nav.interviews'), icon: Video },
    { path: '/references', label: t('nav.references'), icon: BookOpen },
    { path: '/feed', label: t('nav.feed'), icon: Users }
  ];

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
    { code: 'ar', label: 'العربية' }
  ];

  return (
    <nav className={`bg-white shadow-md relative z-50 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center group">
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/7/7d/Microsoft_.NET_logo.svg"
                alt=".NET Logo"
                className="h-8 w-8 sm:h-10 sm:w-10 transition-transform group-hover:scale-110"
              />
              <span className="ml-2 text-lg sm:text-xl font-bold bg-gradient-to-r from-[#512BD4] to-[#190B4D] bg-clip-text text-transparent">
                .NET Mastery
              </span>
            </Link>
          </div>

          <div className="flex items-center sm:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-600 hover:text-[#512BD4] transition-colors"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          <div className="hidden sm:flex items-center space-x-6">
            {navItems.map(({ path, label, icon: Icon }) => (
              <Link 
                key={path}
                to={path} 
                className={`${isActive(path)} transition-colors duration-200 flex items-center space-x-1 px-3 py-2 rounded-lg hover:bg-gray-50`}
              >
                <Icon className="w-4 h-4" />
                <span>{label}</span>
              </Link>
            ))}
            <button
              onClick={startTour}
              className="text-gray-600 hover:text-purple-600 flex items-center space-x-1"
            >
              <HelpCircle className="w-5 h-5" />
              <span>Tour</span>
            </button>
            <div className="relative group">
              <button className="flex items-center space-x-1 text-gray-600 hover:text-purple-600">
                <Globe className="w-5 h-5" />
                <span>{languages.find(l => l.code === language)?.label}</span>
              </button>
              <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-lg py-2 hidden group-hover:block">
                {languages.map(({ code, label }) => (
                  <button
                    key={code}
                    onClick={() => setLanguage(code as 'en' | 'fr' | 'ar')}
                    className={`w-full px-4 py-2 text-left hover:bg-gray-50 ${
                      language === code ? 'text-purple-600' : 'text-gray-600'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="sm:hidden py-2 absolute top-full left-0 right-0 bg-white shadow-lg border-t border-gray-100">
            <div className="space-y-1 px-4">
              {navItems.map(({ path, label, icon: Icon }) => (
                <Link 
                  key={path}
                  to={path} 
                  className={`flex items-center space-x-2 px-4 py-3 rounded-lg ${
                    isActive(path)
                  } hover:bg-gray-50`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Icon className="w-5 h-5" />
                  <span>{label}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}